import _get from "lodash/get";
import _isString from "lodash/isString";

import { API_HOST, API_PATH } from "@/src/config/_entrypoint";
import SubmissionError from "@/src/error/SubmissionError";
import { ApiRequestOptions, ValidationError } from "@/src/types/ApiClient";

const apiGet = (iri: string, filters: Record<string, any> = {}, options: ApiRequestOptions = {}) => {
  const searchParams = new URLSearchParams(filters);
  return apiCall("GET", iri + "?" + searchParams, options);
};

const apiPut = (iri: string, data: Record<string, any>, options: ApiRequestOptions = {}) => {
  options.body = JSON.stringify(data);
  return apiCall("PUT", iri, options);
};

const apiPatch = (iri: string, data: Record<string, any>, options: ApiRequestOptions = {}) => {
  options.body = JSON.stringify(data);
  return apiCall("PATCH", iri, options);
};

const apiPost = (iri: string, data: Record<string, any>, options: ApiRequestOptions = {}, parseJson = true) => {
  options.body = JSON.stringify(data);
  return apiCall("POST", iri, options, parseJson);
};

const apiDelete = (iri: string, options = {}) => {
  return apiCall("DELETE", iri, options);
};

const apiCall = async (method: "GET" | "PUT" | "POST" | "DELETE" | "PATCH", iri: string, options: ApiRequestOptions, parseJson = true) => {
  const url = API_HOST + API_PATH + iri;

  options.method = method;
  options = defaultOptions(options);

  const response = await fetch(url, options);
  if (response.ok && response.status === 204) {
    return null;
  }

  if (!parseJson) {
    return response;
  }

  const json = await response.json();
  if (!response.ok) {
    throw handleError(json, response);
  }

  return json;
};

const getToken = async (credentials: { email: string; password: string }, options = { persist: true, throwError: true }) => {
  const json = await apiPost("/login_check", credentials);
  const token = _get(await json, "token");

  if (!_isString(token)) {
    if (options.throwError) {
      throw new Error("Der Login ist fehlgeschlagen. Bitte überprüfen Sie Ihre Daten.");
    }
  }

  if (options.persist) {
    localStorage.setItem("token", token);
  }
  return token;
};

const defaultOptions = (options: RequestInit & { headers?: Headers }) => {
  if ("undefined" === typeof options.headers) {
    options.headers = new Headers();
  }

  if (null === options.headers.get("Accept")) {
    options.headers.set("Accept", "application/json");
  }

  if ("undefined" !== options.body && !(options.body instanceof FormData) && null === options.headers.get("Content-Type")) {
    options.headers.set("Content-Type", "application/ld+json");
  }

  if (options.method?.toUpperCase() === "PATCH" && !(options.body instanceof FormData)) {
    options.headers.set("Content-Type", "application/merge-patch+json");
  }

  if (options.headers.get("Content-Type") === "multipart/form-data") {
    options.headers.delete("Content-Type");
  }

  const token = localStorage.getItem("token");
  if (null !== token && null === options.headers.get("Authorization")) {
    options.headers.set("Authorization", "Bearer " + token);
  }

  const impersonate = localStorage.getItem("impersonate");
  if (impersonate) {
    options.headers.set("X-Switch-User", impersonate);
  }

  return options;
};

interface ErrorResponse {
  violations?: { propertyPath: string; message: string }[];
  "hydra:description"?: string;
}

const handleError = (json: ErrorResponse, response: Response) => {
  const errorMessage = json["hydra:description"] || response.statusText;
  if (!json.violations) {
    return new Error(errorMessage);
  }

  let errors: ValidationError[] = json.violations.map((violation) => ({
    field: violation.propertyPath,
    message: violation.message,
  }));

  errors.push({ field: "_error", message: errorMessage });

  return new SubmissionError(errors);
};

class ApiClient {
  async getToken(credentials: { email: string; password: string }) {
    return getToken(credentials);
  }

  async get(iri: string, options: ApiRequestOptions = {}) {
    return apiCall("GET", iri, options);
  }

  async post(iri: string, options: ApiRequestOptions = {}) {
    return apiCall("POST", iri, options);
  }

  async put(iri: string, options: ApiRequestOptions = {}) {
    return apiCall("PUT", iri, options);
  }

  async patch(iri: string, options: ApiRequestOptions = {}) {
    return apiCall("PATCH", iri, options);
  }

  async delete(iri: string, options: ApiRequestOptions = {}) {
    return apiCall("DELETE", iri, options);
  }
}

export default new ApiClient();

export { apiGet, apiPut, apiPatch, apiPost, apiDelete, apiCall, getToken };
